/**
 * De onde saem as ROTAS que as tools de front `navegar` e `abrir_cliente`
 * empurram (spec-001 `plataforma-por-chat`, [T015] / US-4).
 *
 * O enum `destino` do backend (`tools/tools_de_front.py`,
 * `DESTINOS_DA_NAVEGACAO`) é um vocabulário de CHAVES, não de rotas: o backend
 * declara o que o modelo pode pedir, e a tradução chave → rota mora aqui. Duas
 * fontes, nesta ordem:
 *
 * 1. **O catálogo da navegação** (`navegacao-v2/constants/navigation-modules.js`)
 *    — 24 dos 25 destinos são `key` de um item de `GESTAO_GROUPS`/`CLIENTE_GROUPS`.
 *    A chave NÃO é o nome da rota: `bi` abre `dashboard-bi`, `consolidador-geral`
 *    abre `carteira`. Resolver por identidade daria 404 silencioso.
 * 2. **`ROUTE_KEYS`/`ROUTE_DEFS`** (`@constants/navigation-routes`) — para o
 *    destino que existe como rota mas não tem item no catálogo. Medido pelo
 *    [T008]: `fee-fixo` é exatamente esse caso (`ROUTE_KEYS.FEE_FIXO`,
 *    `navigation-routes.js:68`). Sem este segundo ramo, o guard reprovaria o
 *    único destino do nosso próprio módulo — o pior lugar possível para uma
 *    lacuna.
 *
 * O índice é montado na primeira consulta e memoizado: os dois catálogos são
 * dados puros, e varrê-los a cada chamada seria trabalho repetido dentro do
 * turno.
 */
import {
  GESTAO_GROUPS,
  CLIENTE_GROUPS,
} from '@modules/navegacao-v2/constants/navigation-modules'
import { resolveDynamicRoute } from '@modules/navegacao-v2/utils/resolve-dynamic-route'
import { ROUTE_DEFS, ROUTE_KEYS } from '@constants/navigation-routes'

/** O destino que só existe em `ROUTE_KEYS` — o caso que nomeia o ramo 2. */
export const DESTINO_DO_FEE_FIXO = ROUTE_KEYS.FEE_FIXO

/**
 * Rotas INTERNAS que um destino de MÓDULO pode declarar via `params.rota` —
 * nunca uma rota livre vinda do modelo (spec-016 [T021], F-1 da review de
 * front). A tool `preparar_ensaio_fee_fixo` (backend) devolve
 * `value.destino = {rota: 'fee-fixo.roleplay.ensaio', query: {...}}` e instrui
 * o modelo a chamar `navegar(destino='fee-fixo', params=query mesclado com
 * {rota: 'fee-fixo.roleplay.ensaio'})` — sem esta allow-list, `params.rota`
 * viraria o `name` da rota SEM checagem nenhuma, e qualquer string ali abriria
 * qualquer tela do app a partir de um parâmetro de tool. Fora da allow-list, a
 * chave é ignorada e o destino cai na raiz do módulo (comportamento de hoje).
 */
export const ROTAS_INTERNAS_PERMITIDAS_POR_DESTINO = {
  [DESTINO_DO_FEE_FIXO]: ['fee-fixo.roleplay.ensaio'],
}

/**
 * A rota interna pedida em `params.rota`, só quando ela pertence à allow-list
 * do `destino`. `null` para "não pedida" OU "pedida fora da allow-list" — o
 * chamador trata os dois casos do mesmo jeito (ignora e usa a rota do módulo).
 *
 * @param {string} destino      Chave do enum de `navegar` (ex.: 'fee-fixo').
 * @param {*} rotaPedida        `params.rota` cru, como veio do modelo.
 * @returns {?string}
 */
export function rotaInternaPermitida(destino, rotaPedida) {
  if (!rotaPedida || typeof rotaPedida !== 'string') return null
  const permitidas = ROTAS_INTERNAS_PERMITIDAS_POR_DESTINO[destino] || []
  return permitidas.indexOf(rotaPedida) !== -1 ? rotaPedida : null
}

/**
 * Destinos do enum que apontam para a tela de OUTRO destino.
 *
 * ★ `tarefas` → `task` (decisão do time-lead no QA-G-1, 27/08). O enum do
 * backend tem as duas chaves e as descreve como telas diferentes — `tarefas`
 * seria a agenda do dia (`daily_tasks`, rota `tarefas`, gateada por
 * `FEATURES.DAILY_TASKS = 'daily-task'`) e `task` o gerenciador. Na prática o
 * modelo escolhe `tarefas` sozinho em pedido PT ("abra as tarefas"), e o
 * escritório que não tem a flag da agenda ia parar no `/404`: medido no
 * `router.afterEach` do QA — `aawz-intelligence.chat-ia` → 3.044 ms → `/404`.
 *
 * ⚠️ O efeito colateral está registrado de propósito: com o alias, a agenda do
 * dia deixa de ser alcançável pelo `navegar`, mesmo no escritório que a tem.
 * Fechar isso de vez é ajuste da DESCRIÇÃO da tool no backend (ensinar o modelo
 * a escolher entre as duas chaves); enquanto ele não vem, o alias é o que
 * impede o consultor de cair numa tela de erro.
 */
const ALIASES_DE_DESTINO = {
  tarefas: ROUTE_KEYS.TASK,
}

let indice = null

function montarIndice() {
  const mapa = {}
  const grupos = GESTAO_GROUPS.concat(CLIENTE_GROUPS)
  grupos.forEach((grupo) => {
    const itens = (grupo && grupo.items) || []
    itens.forEach((item) => {
      if (!item || !item.key || !item.routeName) return
      // Primeiro a registrar vence: `GESTAO` antes de `CLIENTE`, que é a ordem
      // em que o próprio catálogo os declara.
      if (!Object.prototype.hasOwnProperty.call(mapa, item.key)) {
        mapa[item.key] = item
      }
    })
  })
  return mapa
}

/**
 * O ITEM do catálogo, com os marcadores (`clienteSkipTo`, `dynamicRoute`).
 * `abrir_cliente` precisa do item, não só do nome: o destino de um módulo na
 * visão de UM cliente é o `clienteSkipTo` (a aba que já filtra pelo cliente),
 * e não a tela agregada de todos.
 */
export function itemDoCatalogo(chave) {
  if (!chave || typeof chave !== 'string') return null
  if (indice === null) indice = montarIndice()
  return indice[chave] || null
}

/**
 * O rótulo humano de um destino do enum — o MESMO nome que o menu da
 * plataforma usa para o módulo, nunca uma segunda tradução ([S-1], verdict de
 * design-conformance da spec-016, 05/09/2026).
 *
 * Primeiro tenta o item do catálogo de navegação (24 dos 25 destinos); para o
 * destino que só existe em `ROUTE_KEYS` (hoje só `fee-fixo` —
 * `DESTINO_DO_FEE_FIXO` — não tem item porque não é aba de nenhum grupo),
 * `itemDoCatalogo` devolve `null` e o cartão da ação oferecida caía no código
 * cru ("fee-fixo" em vez de "Migração Fee Fixo"). O fallback é `ROUTE_DEFS`,
 * a MESMA fonte que `rotaDoDestino` já lê — nunca uma segunda tradução que
 * divergiria dela no próximo ajuste.
 *
 * @param {string} chave
 * @returns {?string} `null` quando nenhuma das duas fontes conhece o destino
 *   — o chamador decide o que fazer (hoje: mostrar o código cru).
 */
export function tituloDoDestino(chave) {
  const item = itemDoCatalogo(chave)
  if (item && item.label) return item.label
  const def = ROUTE_DEFS[chave]
  return (def && def.title) || null
}

/** O param que carrega o cliente quando a rota não declara outro. */
export const PARAM_PADRAO_DO_CLIENTE = 'id'

/**
 * A rota de um módulo NA VISÃO DE UM CLIENTE — `{name, paramKey}`.
 *
 * Precedência, do mais específico ao mais genérico:
 *
 * 1. **`clienteDeepLink`** ([spec-009 T018]) — a rota que carrega o cliente na
 *    PRÓPRIA URL. Hoje só o Consolidador tem: `carteira.minha.cliente`, com o
 *    param `id_cliente_route`. Ela vem primeiro porque é a única forma de abrir
 *    a carteira DE UM cliente sem depender de quem estava no contexto antes.
 * 2. **`clienteSkipTo`** — a aba que auto-filtra o cliente: `cli-crm` aponta
 *    para `crm` (o painel agregado de TODOS os clientes) e traz
 *    `clienteSkipTo: 'crm.relationship'`. Abrir a agregada com um cliente em
 *    foco seria levar o usuário ao lugar errado com a impressão de ter acertado.
 * 3. `routeName` — o destino normal do módulo.
 *
 * ★ **O retorno é `{name, paramKey}`, e não mais só o nome.** O param do cliente
 * não é o mesmo em toda rota: `cliente-perfil` declara `:id`, e o deep link da
 * carteira declara `:id_cliente_route`. Quem monta o `params` (o `abrir_cliente`,
 * único consumidor) precisa do nome CERTO — um `{id}` numa rota que pede outro
 * param é descartado pelo vue-router **sem erro nenhum**, e a tela abre para o
 * cliente que já estava lá.
 *
 * @returns {?{name: string, paramKey: string}}
 */
export function rotaDoModuloNoCliente(chave) {
  const item = itemDoCatalogo(chave)
  if (!item) return null

  const deepLink = item.clienteDeepLink
  if (deepLink && deepLink.routeName) {
    return {
      name: deepLink.routeName,
      paramKey: deepLink.paramKey || PARAM_PADRAO_DO_CLIENTE,
    }
  }

  const name = item.clienteSkipTo || item.routeName || null
  if (!name) return null
  return { name: name, paramKey: PARAM_PADRAO_DO_CLIENTE }
}

/**
 * A rota de um destino do enum do backend.
 *
 * ★ **`routeName` nem sempre é a rota** (QA-G-1 da spec-010). Três itens do
 * catálogo — Comissões, Tesouraria e Consolidador — têm destino DINÂMICO: o
 * `routeName` deles é o rótulo do módulo, e a tela real sai de um resolvedor
 * que lê as flags do escritório. Medido: `ROUTE_DEFS[COMISSOES].routeName` é
 * `'comissoes'`, e **não existe rota com esse nome** — as reais são
 * `comissoes.visao-geral` e `comissoes.fechamentos`. Ler o `routeName` cru
 * mandava o `router.push` para uma rota inexistente, e a plataforma caía no
 * `/404` com a tool dizendo ao modelo que abriu a tela.
 *
 * Quem resolve é `resolveDynamicRoute`, o MESMO helper que a nav-bar usa no
 * clique — é o que mantém o destino da IA e o do usuário idênticos. Uma segunda
 * régua aqui seria a que diverge no dia em que a flag mudar de significado.
 *
 * @param {string} destino  Chave do catálogo (ou de `ROUTE_KEYS`).
 * @param {Object} [ctx]    `{store, vm}` — de onde saem as flags e o usuário.
 *                          Sem ele, o resolvedor cai no ramo sem acesso, que é
 *                          uma rota real (nunca o rótulo do módulo).
 * @returns {?string} `name` da rota, ou `null` quando o destino não existe.
 */
export function rotaDoDestino(destino, ctx) {
  if (!destino || typeof destino !== 'string') return null
  if (indice === null) indice = montarIndice()

  const chave = ALIASES_DE_DESTINO[destino] || destino

  if (Object.prototype.hasOwnProperty.call(indice, chave)) {
    return resolveDynamicRoute(indice[chave], contextoDaResolucao(ctx))
  }

  const def = ROUTE_DEFS[chave]
  return (def && def.routeName) || null
}

/**
 * A rota EXISTE no router desta superfície?
 *
 * ★ Perguntar ANTES do push é o ponto (QA-G-1, round 2). Empurrar e conferir
 * depois já levou o consultor à tela de erro: o `afterEach` do QA mostra a
 * navegação para `/404` acontecendo, e só então a tool marcando `output-denied`
 * — fail-closed que chega tarde é fail-closed que não serviu para nada.
 *
 * `getRoutes()` é o mesmo caminho que `$canAccessRoute` (`main.js`) usa. Router
 * que não o expõe (dublê antigo, superfície exótica) devolve `true`: sem a
 * lista não há o que afirmar, e recusar por isso inventaria um destino morto.
 */
export function rotaExisteNoRouter(ctx, name) {
  const router = ctx && ctx.router
  const lista =
    router && typeof router.getRoutes === 'function'
      ? router.getRoutes()
      : router && router.matcher && typeof router.matcher.getRoutes === 'function'
      ? router.matcher.getRoutes()
      : null
  if (!lista) return true
  return lista.some((rota) => rota && rota.name === name)
}

/**
 * O `{access, currentUser}` que `resolveDynamicRoute` espera, extraído do
 * contexto de execução das tools de front.
 *
 * ★ `isCliente` fica FALSO de propósito: o `clienteSkipTo` é a régua do
 * `abrir_cliente` (a aba que já filtra UM cliente), e `navegar` abre a tela
 * agregada. Ligá-lo aqui faria a mesma chave levar a telas diferentes conforme
 * o contexto, sem que o modelo tivesse pedido isso.
 */
function contextoDaResolucao(ctx) {
  const store = ctx && ctx.store
  const vm = ctx && ctx.vm
  const getters = (store && store.getters) || {}
  const currentUser =
    vm && typeof vm.$currentUser === 'function' ? vm.$currentUser() : {}
  return {
    access: getters['parameters/show'] || {},
    currentUser: currentUser || {},
    isCliente: false,
  }
}

/** Os destinos que este front sabe abrir — entra na mensagem de recusa. */
export function destinosConhecidos() {
  if (indice === null) indice = montarIndice()
  return Object.keys(indice).concat([DESTINO_DO_FEE_FIXO]).sort()
}

/**
 * O contexto de execução chegou? Sem `router` não há o que empurrar, e o
 * handler precisa dizer isso em vez de estourar `undefined.push`.
 */
export function temContextoDeNavegacao(ctx) {
  return Boolean(ctx && ctx.router && typeof ctx.router.push === 'function')
}

export const MOTIVO_SEM_CONTEXTO =
  'a tela que você está usando não registrou o contexto de execução, então não ' +
  'é possível navegar por aqui.'

/**
 * `$canAccessRoute` da superfície, com fail-OPEN quando ele não veio.
 *
 * Fail-open é a escolha certa AQUI (e o oposto da flag do registry, que é
 * fail-closed): o guard de rota da plataforma roda de qualquer forma no
 * `router.push`. Reprovar por falta do injetado transformaria "a superfície não
 * registrou o provedor" em "você não tem acesso a esta tela" — uma mentira
 * sobre permissão, que é o pior tipo de motivo para o modelo repetir ao usuário.
 */
export function podeAcessar(ctx, routeName) {
  const checa = ctx && ctx.canAccessRoute
  if (typeof checa !== 'function') return true
  return Boolean(checa(routeName))
}

/**
 * Toda tela que a IA abre (cartão de leitura OU tool `navegar`/`abrir_cliente`)
 * passa por `empurrarRota` — é o único funil. Uma constante aqui, e não uma
 * flag espalhada pelos 6 `acoes.js` + 2 tools de front que a chamam.
 *
 * `true`: nova aba (não perde o fio da conversa ao explorar a tela).
 * `false`: volta ao `router.push` de sempre — a mesma navegação SPA in-place.
 */
export const ABRIR_TELA_EM_NOVA_ABA = true

/**
 * O `window.open` automático (sem gesto do usuário) é bloqueado pelo
 * navegador — é exatamente o caso da navegação AUTOMÁTICA disparada pelo
 * modelo (spec-001, feedback do owner 04/09). `globalThis.open` devolve
 * `null` quando isso acontece, e SEM checar o retorno o chamador reportava
 * `aberto: true` com nada aberto. O clique do usuário no botão de fallback
 * não sofre disso — é gesto real, então o mesmo `window.open` abre.
 *
 * ⚠️ O retorno só distingue bloqueio de sucesso SEM `noopener` na chamada:
 * com ele, por spec (HTML "window open steps"), `window.open` devolve `null`
 * MESMO quando a aba abre — e todo clique em "Abrir" reportava bloqueio sobre
 * uma aba aberta. Ver `abrirAbaDesvinculada`.
 */
export const MOTIVO_POPUP_BLOQUEADO =
  'o navegador bloqueou a nova aba (pop-up). Abra pelo botão do cartão.'

/**
 * O aviso para o USUÁRIO que clicou num CTA de cartão (`<modulo>/ia/acoes.js`,
 * curadoria) e teve a aba barrada. Diferente de `MOTIVO_POPUP_BLOQUEADO`, que
 * fala ao modelo: aqui o clique já era o gesto real, então a saída é liberar
 * o pop-up, não "clicar no botão".
 */
export const AVISO_ABA_BLOQUEADA =
  'O navegador bloqueou a nova aba. Libere pop-ups para este site e clique de novo.'

/**
 * Snack de aviso quando `empurrarRota` devolveu `bloqueado` — ponto único dos
 * consumidores que abrem tela por clique. Devolve o resultado intacto, para
 * encadear no `.then`.
 */
export function avisarAbaBloqueada(vm, resultado) {
  const bloqueado = Boolean(resultado && resultado.bloqueado)
  if (bloqueado && vm && typeof vm.$showSnack === 'function') {
    vm.$showSnack({ text: AVISO_ABA_BLOQUEADA, color: 'warning', button: true })
  }
  return resultado
}

/**
 * Abre `href` numa aba nova e devolve a janela, ou `null` quando o navegador
 * bloqueou.
 *
 * Exceção documentada à regra "`window.open` exige `noopener,noreferrer`"
 * (`CLAUDE.md` da raiz § Frontend Security; precedente `finia/CLAUDE.md` §7):
 * com `noopener` o retorno é `null` por spec mesmo em sucesso, e este funil
 * PRECISA do retorno para dizer ao modelo se abriu. O substituto é
 * `aba.opener = null` logo após o `open` — a janela ainda é `about:blank`
 * (same-origin), e a atribuição marca o browsing context como disowned de
 * forma permanente, o que é o que o `noopener` protegia. O `noreferrer` não
 * faz falta: `href` vem do `router.resolve`, então o destino é a PRÓPRIA
 * origem — o Referer não sai do app.
 */
function abrirAbaDesvinculada(href) {
  const aba = globalThis.open(href, '_blank')
  if (!aba) return null
  aba.opener = null
  return aba
}

/**
 * `router.push` tolerante ao push REDUNDANTE — ou `window.open`, conforme
 * `ABRIR_TELA_EM_NOVA_ABA`.
 *
 * O vue-router 3 REJEITA a navegação para a rota em que já se está
 * (`NavigationDuplicated`). Sem este ramo, pedir a tela que já está aberta
 * viraria `output-error`, e o modelo diria ao usuário que não conseguiu abrir a
 * tela que está na frente dele. Qualquer OUTRA falha propaga: um `aberto: true`
 * sobre um push que não aconteceu é exatamente o desfecho silencioso que a
 * spec-001 existe para eliminar.
 *
 * Em nova aba não existe o problema do push redundante (cada clique abre uma
 * aba nova, ainda que para a MESMA rota) — o `.catch` de `NavigationDuplicated`
 * só faz sentido no ramo do `router.push`.
 *
 * @param {Object} ctx
 * @param {Object} rota
 * @param {boolean} [abrirEmNovaAba]  Sobrescreve `ABRIR_TELA_EM_NOVA_ABA` — só
 *   o chamador (`navegar.js`) passa isto, e só em teste (produção nunca varia
 *   a constante). Sem ele, cai no default do módulo. Existe porque `export
 *   const ABRIR_TELA_EM_NOVA_ABA` é uma ligação IMUTÁVEL: um `jest.doMock`
 *   que sobrescreve só o valor exportado não muda o que esta função lê do
 *   PRÓPRIO escopo léxico — sem o parâmetro, o ramo `router.push` (reversão
 *   documentada acima) fica InTESTÁVEL para sempre (F-4 da review, spec-016
 *   [T021]).
 * @returns {Promise<{aberto: boolean, href?: string, bloqueado?: boolean, motivo?: string}>}
 */
export function empurrarRota(ctx, rota, abrirEmNovaAba) {
  const emNovaAba =
    abrirEmNovaAba === undefined ? ABRIR_TELA_EM_NOVA_ABA : abrirEmNovaAba
  if (emNovaAba) {
    const resolvido = ctx.router.resolve(rota)
    const href = resolvido && resolvido.href
    if (!href) return Promise.reject(new Error('rota sem href para abrir'))
    const janela = abrirAbaDesvinculada(href)
    if (!janela) {
      return Promise.resolve({
        aberto: false,
        bloqueado: true,
        href: href,
        motivo: MOTIVO_POPUP_BLOQUEADO,
      })
    }
    return Promise.resolve({ aberto: true, href: href })
  }

  return Promise.resolve()
    .then(() => ctx.router.push(rota))
    .then(() => ({ aberto: true }))
    .catch((erro) => {
      const nome = erro && erro.name
      if (nome === 'NavigationDuplicated') return { aberto: true }
      throw erro
    })
}
